/**
 * AuthField
 *
 * The text field used by the sign-in screens. The shared `FormInput` is used by
 * three dozen module forms and is styled for dense data entry with a fixed blue
 * focus ring; the authentication panel needs a larger control, a scheme-driven
 * accent and fuller ARIA wiring, so it gets its own field rather than a pile of
 * overrides on the shared one.
 *
 * Accessibility contract:
 *  - the label is a real `<label for>`, never a placeholder;
 *  - errors are announced (`role="alert"`) and bound with `aria-describedby`,
 *    and the input carries `aria-invalid`;
 *  - the password reveal is a real button with an `aria-label` that names the
 *    action and an `aria-pressed` state, so it is reachable and readable;
 *  - the focus ring sits on the wrapper (see `auth-field.css`) and is always
 *    visible — it is never traded away for a cleaner border.
 */

import React, { forwardRef, useId, useState } from 'react';
import './auth-field.css';

export interface AuthFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  /** Remix icon class for the leading glyph, e.g. `ri-user-line`. */
  iconClass?: string;
  error?: string;
  /** Focus border colour. Supplied by the form's colour scheme. */
  accent?: string;
  /** Focus halo colour — the accent at low alpha. */
  accentGlow?: string;
}

const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(
  (
    {
      label,
      iconClass,
      error,
      accent = '#123262',
      accentGlow = 'rgba(18,50,98,0.13)',
      type = 'text',
      className = '',
      id,
      required,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || `auth-field-${generatedId}`;
    const errorId = `${inputId}-error`;
    const [revealed, setRevealed] = useState(false);

    const isPassword = type === 'password';
    const resolvedType = isPassword ? (revealed ? 'text' : 'password') : type;

    return (
      <div className="space-y-1">
        <label
          htmlFor={inputId}
          className="block text-[13px] font-semibold text-slate-700 dark:text-gray-300"
        >
          {label}
          {required && (
            <span className="ml-0.5 text-red-500" aria-hidden="true">
              *
            </span>
          )}
        </label>

        <div
          className={`dtcp-field relative flex items-center rounded-lg ${error ? 'dtcp-field--error' : ''}`}
          style={
            {
              '--dtcp-field-accent': accent,
              '--dtcp-field-glow': accentGlow,
            } as React.CSSProperties
          }
        >
          {iconClass && (
            <i
              className={`${iconClass} pointer-events-none absolute left-3.5 text-[17px] text-slate-400 dark:text-gray-500`}
              aria-hidden="true"
            />
          )}

          <input
            ref={ref}
            id={inputId}
            type={resolvedType}
            required={required}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            className={`h-11 w-full rounded-lg bg-transparent text-[15px] text-slate-900 placeholder-slate-400 dark:text-white dark:placeholder-gray-500 ${
              iconClass ? 'pl-10' : 'pl-3.5'
            } ${isPassword ? 'pr-11' : 'pr-3.5'} ${className}`}
            {...props}
          />

          {isPassword && (
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              aria-label={revealed ? 'Hide password' : 'Show password'}
              aria-pressed={revealed}
              aria-controls={inputId}
              className="absolute right-1.5 flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 dark:hover:bg-gray-800 dark:hover:text-gray-300"
            >
              <i
                className={`${revealed ? 'ri-eye-off-line' : 'ri-eye-line'} text-[17px]`}
                aria-hidden="true"
              />
            </button>
          )}
        </div>

        {error && (
          <p
            id={errorId}
            role="alert"
            className="dtcp-nudge flex items-start gap-1.5 text-[12.5px] font-medium text-red-600 dark:text-red-400"
          >
            <i className="ri-error-warning-line mt-px text-[13px]" aria-hidden="true" />
            <span>{error}</span>
          </p>
        )}
      </div>
    );
  }
);

AuthField.displayName = 'AuthField';

export default AuthField;
